/* eslint-disable no-underscore-dangle */
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { MatTableDataSource } from '@angular/material/table';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import * as moment from 'moment';
import * as momentTz from 'moment-timezone';
import { from, Observable } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { Interpretation } from '../awv/awv-patient-responses/awv-patient-responses.model';
import { Client } from '../models/client.model';
import { Patient } from '../models/patient.model';
import { TimeZone, User } from '../models/user.model';
import { VitalFlag } from '../models/vitals/vital-flag.model';
import {
  SurveyConf,
  SurveyDetail,
} from '../shared/components/client-detail/client-demographics/survey-conf/survey-conf.model';
import { DateTimeWelbyCustomPipe } from '../shared/pipes/date-time-custom.pipe';
import {
  ActiveDeviceStatus,
  DeviceStatus,
  isSmartMeterStatus,
  isTenoviGateway,
} from './device-mapping/models/device.model';
import { OrderVO } from './device-ordering/value-objects/order.vo';
import { FirestoreService } from './firestore.service';
import { TrackingData, TrackingProvider } from './models/trackingInfo.model';
import { SnackService } from './snack.service';
import { UntypedFormControl } from '@angular/forms';
interface ElasticSearchTimeStamp {
  _seconds: number;
  _nanoseconds: number;
}

type GeneralDate = firebase.firestore.Timestamp | Date | ElasticSearchTimeStamp;
@Injectable({
  providedIn: 'root',
})
export class UtilsService {
  surveyFormTest = 'TFJWCOqu';
  constructor(
    private datePipe: DateTimeWelbyCustomPipe,
    private fs: FirestoreService,
    private firestore: AngularFirestore,
    private http: HttpClient,
    private snackService: SnackService
  ) {}

  /**
   * Get American phone number format
   */
  getPhoneNumberFormated(phone: string): string {
    const numbers = [];
    if (!phone) {
      return '';
    }
    numbers.push(phone.substring(0, 3));
    if (phone.substring(3, 6) !== '') {
      numbers.push(phone.substring(3, 6));
    }
    if (phone.substring(6, 10) !== '') {
      numbers.push(phone.substring(6, 10));
    }
    return numbers.join('-').substring(0, 12);
  }

  /**
   * Format phone number for display with proper US formatting
   * Supports 10-digit and 11-digit (with country code) numbers
   */
  formatPhoneNumber(phoneNumber: string): string {
    if (!phoneNumber) {
      return 'Unknown Number';
    }

    const cleaned = phoneNumber.replace(/\D/g, '');

    if (cleaned.length === 10) {
      return `(${cleaned.substring(0, 3)}) ${cleaned.substring(3, 6)}-${cleaned.substring(6)}`;
    } else if (cleaned.length === 11 && cleaned.startsWith('1')) {
      return `+1 (${cleaned.substring(1, 4)}) ${cleaned.substring(4, 7)}-${cleaned.substring(7)}`;
    }

    return phoneNumber;
  }

  copyKey2Clipboard(value: string): void {
    navigator.clipboard.writeText(value);
    this.snackService.genericSnackBar(`${value} copied!`, ['success-snackbar'], 2000);
  }

  getTrackingInfo(order: OrderVO | any, trackingProviders: TrackingProvider): TrackingData {
    const carrier = order?.orderStatus?.order?.carrier?.toLowerCase() ?? 'none';
    const lineItems = order?.orderStatus?.order?.lines ?? 'none';
    const tracking_number = order?.orderStatus?.order?.tracking_number ?? 'none';
    const trackingUrl =
      `${trackingProviders[carrier]?.['url']}` !== 'none'
        ? `${trackingProviders[carrier]?.['url']}${tracking_number}`
        : (order?.orderStatus?.tracking_link ?? 'none');
    const trackingIcon =
      trackingProviders[carrier]['icon'] !== 'none.png' ? trackingProviders[carrier]['icon'] : 'delivery.png';
    const trackingInfo = {
      carrier,
      lineItems,
      tracking_number,
      trackingUrl,
      trackingIcon,
    };
    return trackingInfo;
  }

  async getTrackingProviders(): Promise<any> {
    return await this.fs.doc$(`welby_configuration/tracking_providers`).pipe(take(1)).toPromise();
  }

  /**
   * Base64 from file
   */
  getBase64FromFile(file: Blob): Promise<any> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const extractaFile = reader && reader.result ? (reader.result as string).split(',')[1] : '';
        resolve(extractaFile);
      };
      reader.onerror = (error) => reject(error);
    });
  }

  /**
   * Get label and color for tier
   *
   * @param tier number
   * @returns
   */
  getTierLabel(tier: number): { label: string; color: string } {
    switch (tier) {
      case 3:
        return { label: 'High', color: 'warn' };
      case 2:
        return { label: 'Medium', color: 'accent' };
      case 1:
        return { label: 'Low', color: 'primary' };
      default:
        return { label: 'Unknown', color: 'default' };
    }
  }

  /**
   * Formats a vital flag measurement value with its appropriate units.
   * For weight measurements in kilograms, converts to pounds.
   * For other measurements, combines measurement values with units.
   *
   * @param alert - The vital flag containing measurement values and units
   * @returns A formatted string representing the measurement with units
   */
  getMeasureLabel(alert: VitalFlag): string {
    // A guide-raised alert (`source: 'care_call_guide'`) carries a DOCUMENTED FINDING rather than a
    // device measurement — "new diuretic dose change: Yes" has no value/units pair to format, so the
    // numeric path below renders it as `0 `, which an NCM reasonably reads as a broken alert or,
    // worse, as a real reading. Prefer what the MA actually recorded.
    //
    // Guarded on BOTH `source` AND `finding_value`, deliberately: this function renders EVERY alert
    // in the product, device-generated included, so every existing alert must take the identical
    // code path it takes today, and a guide alert with partial data must fall through rather than
    // return an empty string.
    if (alert.source === 'care_call_guide' && alert.finding_value) {
      return alert.finding_value;
    }
    if (alert.alert_category === 'Weight') {
      if (alert.units === 'kgs' || alert.units === 'kGs') {
        // Through `formatMeasurement` like every other branch. `toFixed(2)` never had the float bug
        // — the conversion is rounded either way — but it PADS: a kg-sourced weight rendered
        // "2.20 lbs" in the same column where an lb-sourced one now renders "2.2 lbs". Two decimal
        // conventions in one column is the kind of difference an NCM reads as significant digits.
        return this.formatMeasurement(alert.measurement_value1 * 2.20462) + ' lbs';
      } else {
        return this.formatMeasurement(alert.measurement_value1) + ' ' + alert.units;
      }
    } else {
      return (
        this.formatMeasurement(alert.measurement_value1) +
        (alert.measurement_value2 ? ' / ' + this.formatMeasurement(alert.measurement_value2) : '') +
        ' ' +
        alert.units
      );
    }
  }

  /**
   * Get date from firebase timestamp
   */
  formatDate(timestamp: any, format = 'lll', timezone?: TimeZone): string {
    try {
      let date;
      if (typeof timestamp === 'number') {
        date = new Date(timestamp);
      } else if (timestamp.toDate) {
        date = timestamp.toDate();
      } else if (timestamp.seconds || timestamp._seconds) {
        date = new Date(timestamp.seconds * 1000 || timestamp._seconds * 1000);
      }
      if (timezone) {
        date = momentTz.tz(date, timezone.nameValue);
        return date.format(format);
      }
      const m = moment(date);
      return m.format(format);
    } catch (error) {
      console.error('Error formatting date', error);
      return 'N/A';
    }
  }

  getTodayDate() {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return now;
  }

  getTodayEODDate(timezone?: TimeZone) {
    const now = timezone ? moment().tz(timezone.nameValue) : moment();
    const todayEODDate = now.endOf('day').toDate();
    return todayEODDate;
  }

  getLastWeekDate(timezone?: TimeZone) {
    const now = timezone ? moment().tz(timezone.nameValue) : moment();
    const lastWeekDate = now.clone().subtract(1, 'week').startOf('isoWeek').toDate();
    const lastWeekEndDate = now.clone().subtract(1, 'week').endOf('isoWeek').toDate();
    return { lastWeekDate, lastWeekEndDate };
  }

  getLastMonthDate(timezone?: TimeZone) {
    const now = timezone ? moment().tz(timezone.nameValue) : moment();
    const lastMonthDate = now.clone().subtract(1, 'month').startOf('month').toDate();
    const lastMonthEndDate = now.clone().subtract(1, 'month').endOf('month').toDate();
    return { lastMonthDate, lastMonthEndDate };
  }

  getThisWeekDate(timezone?: TimeZone) {
    const now = timezone ? moment().tz(timezone.nameValue) : moment();
    const thisWeekDate = now.clone().startOf('isoWeek').toDate();
    const thisWeekEndDate = now.clone().endOf('isoWeek').toDate();
    return { thisWeekDate, thisWeekEndDate };
  }

  getThisMonthDate(timezone?: TimeZone) {
    const now = timezone ? moment().tz(timezone.nameValue) : moment();
    const thisMonthDate = now.clone().startOf('month').toDate();
    const thisMonthEndDate = now.clone().endOf('month').toDate();
    return { thisMonthDate, thisMonthEndDate };
  }

  getLast90DaysDate(timezone?: TimeZone) {
    const now = timezone ? moment().tz(timezone.nameValue) : moment();
    const lastMonthDate = now.subtract(3, 'month').startOf('day').toDate();
    return lastMonthDate;
  }

  getLast180DaysDate(timezone?: TimeZone) {
    const now = timezone ? moment().tz(timezone.nameValue) : moment();
    const lastMonthDate = now.subtract(6, 'month').startOf('day').toDate();
    return lastMonthDate;
  }

  getThreeDaysAfterToday() {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return new Date(now.getFullYear(), now.getMonth(), now.getDate() + 3);
  }

  getA1CfromeAG(mgdl: number) {
    return ((mgdl + 46.7) / 28.7).toFixed(3);
  }

  sortUsersByAlphabeticOrder(userA: User, userB: User) {
    return userA.firstName > userB.firstName ? 1 : userB.firstName > userA.firstName ? -1 : 0;
  }

  sortUsersByAlphabeticLastNameOrder(userA: User, userB: User) {
    return userA.lastName > userB.lastName ? 1 : userB.lastName > userA.lastName ? -1 : 0;
  }

  sortClientsByAlphabeticOrder(clientA: Client, clientB: Client) {
    return clientA.client_name > clientB.client_name ? 1 : clientB.client_name > clientA.client_name ? -1 : 0;
  }

  getQueryStringFromParams(params): string {
    const query =
      Object.keys(params).length > 1
        ? Object.keys(params).reduce((prev, current, index) => {
            let result: string;
            if (index === 1) {
              result = `?${prev}=${params[prev]}&${current}=${params[current]}`;
            } else {
              result = `${prev}&${current}=${params[current]}`;
            }
            return result;
          })
        : `?${Object.keys(params)[0]}=${params[Object.keys(params)[0]]}`;
    return query;
  }

  isGreaterThanYear(lastDateForm): boolean {
    const date = lastDateForm;
    if (!date) {
      return true;
    } else {
      const dateMillis = date.seconds * 1000;
      const dateMoment = moment(dateMillis);
      const currentDate = moment();
      const diffInYears = currentDate.diff(dateMoment, 'years');
      return diffInYears >= 1;
    }
  }

  getChunksWhenArrayLengthIsGreaterThanMaxValue(array: string[]) {
    const MAX_IN_VALUES = 8;
    const chunks = [];

    for (let i = 0; i < array.length; i += MAX_IN_VALUES) {
      chunks.push(array.slice(i, i + MAX_IN_VALUES));
    }
    return chunks;
  }

  getChunksObsWhenArrayLengthIsGreaterThanMaxValue(array: string[]) {
    return from(this.getChunksWhenArrayLengthIsGreaterThanMaxValue(array));
  }

  /**
   * Check if survey is enabled. Only can be done once per month
   */
  async isSurveyEnabled(lastSurveyDate: any, clientId: string): Promise<boolean> {
    if (!lastSurveyDate) {
      return true;
    }
    let maxDiffDays: number;
    const clientSurveyConf = await this.fs.doc$(`client_survey_conf/${clientId}`).pipe(take(1)).toPromise();
    if (clientSurveyConf) {
      maxDiffDays = clientSurveyConf['limit_range_days'];
    } else {
      const welbySurveyConf = await this.fs.doc$(`welby_configuration/default_survey_conf`).pipe(take(1)).toPromise();
      maxDiffDays = welbySurveyConf['limit_range_days'];
    }
    const currentDate = new Date();
    const lastSurveyDateObj = lastSurveyDate.toDate();
    const diffTime = Math.abs(currentDate.getTime() - lastSurveyDateObj.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays >= maxDiffDays;
  }

  /**
   * Return patient with format dates from ES
   *
   * @param patient patient
   * @param patient patient
   * @returns Patient
   */
  addDefaultTZ(patient: Patient): Patient {
    if (patient?.timezone?.abbr) {
      return patient;
    }
    patient.timezone = {
      abbr: 'PDT',
      group: 'US',
      name: 'US/Pacific (-07:00)',
      nameValue: 'US/Pacific',
      timeValue: '-07:00',
    };
    return patient;
  }

  mapDates(patient: Patient): Patient {
    if (patient.dates?.last_measurement_date) {
      patient.last_measurement_date =
        this.datePipe.transform(patient.dates?.last_measurement_date?._seconds * 1000, patient.timezone?.nameValue) ??
        'N/A';
    } else {
      patient.last_measurement_date = 'N/A';
    }
    if (patient.dates?.last_assessment) {
      patient.last_assessment =
        this.datePipe.transform(patient.dates?.last_assessment?._seconds * 1000, patient.timezone?.nameValue) ?? 'N/A';
    } else {
      patient.last_assessment = 'N/A';
    }
    if (patient.dates?.start_date) {
      patient.dates.start_date =
        this.datePipe.transform(patient.dates?.start_date?._seconds * 1000, patient.timezone?.nameValue) ?? 'N/A';
    }
    if (patient.lastContact) {
      patient.lastContact.date =
        this.datePipe.transform(patient.lastContact?.date, patient.timezone?.nameValue) ?? 'N/A';
    }
    return patient;
  }

  /**
   * Normalizes the sort date accessors for the patient list.
   *
   * @param patientList - The MatTableDataSource of patients.
   * @returns void
   */
  normalizeSortDateAccessors(patientList: MatTableDataSource<Patient>): void {
    const dateformat = 'MM/DD/YY h:mm A z';
    patientList.sortingDataAccessor = (item, property) => {
      switch (property) {
        case 'last_measurement_date':
          return item.last_measurement_date && item.last_measurement_date !== 'N/A'
            ? momentTz.tz(item.last_measurement_date, dateformat, item.timezone.nameValue)?.toDate()
            : new Date(2000, 0, 1);
        case 'last_contact':
          return item.lastContact && item.lastContact.date !== 'N/A'
            ? momentTz.tz(item.lastContact.date, dateformat, item.timezone.nameValue)?.toDate()
            : new Date(2000, 0, 1);
        case 'rpm_time':
          return item.compliance.timeReportByPatient.rpmTotal;
        case 'ccm_time':
          return item.compliance.timeReportByPatient.ccmTotal;
        case 'pcm_time':
          return item.compliance.timeReportByPatient.pcmTotal;
        case 'days_measured':
          return item.compliance.vitalsReport.uniqueMeasures;
        default:
          return item[property];
      }
    };
  }

  formatDuration(duration: string | number | null | undefined): string {
    if (!duration) {
      return 'N/A';
    }

    // If duration is a number (seconds), format it
    if (typeof duration === 'number') {
      const minutes = Math.floor(duration / 60);
      const seconds = duration % 60;
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    }

    return duration.toString();
  }

  async showpdf(response) {
    const file = this.createPdfFile(response);
    const fileURL = URL.createObjectURL(file);
    window.open(fileURL);
  }

  createPdfFile(response) {
    return new Blob([response], { type: 'application/pdf' });
  }

  normalizedTwilioLines(user: User) {
    return user.twilio_line ? (Array.isArray(user.twilio_line) ? user.twilio_line : [user.twilio_line]) : [];
  }

  addLeadingZero(n: number) {
    return n < 10 ? '0' + n : '' + n;
  }

  /**
   * Retrieves the survey configuration for a client. If no client-specific configuration exists,
   * returns the default configuration.
   *
   * @param clientId - The ID of the client to get survey configuration for
   * @returns A Promise that resolves to a SurveyConf object containing:
   *  - limit_range_days: Number of days between surveys
   *  - surveys: Object containing survey configurations with:
   *    - formId: TypeForm ID for the survey
   *    - title: Display title for the survey
   *  - isClientConf: Boolean indicating if this is a client-specific configuration
   * ```
   */
  async getSurveyConf(clientId: string): Promise<SurveyConf> {
    if (!clientId) {
      return { limit_range_days: 30, surveys: {} };
    }
    const clientSurveyConf = await this.fs.doc$<SurveyConf>(`client_survey_conf/${clientId}`).pipe(take(1)).toPromise();
    if (clientSurveyConf) {
      return { ...clientSurveyConf, isClientConf: true };
    } else {
      const defaultConf = await this.fs
        .doc$<SurveyConf>(`welby_configuration/default_survey_conf`)
        .pipe(take(1))
        .toPromise();
      return { ...defaultConf, isClientConf: false };
    }
  }

  /**
   * Retrieves patient-specific surveys from a client's survey configuration.
   * A survey is considered patient-specific if its key contains the word 'patient'.
   *
   * @param clientId - The ID of the client to get patient surveys for
   * @returns A Promise that resolves to an array of survey keys that are patient-specific
   */
  async getSurveyForPatients(clientId: string): Promise<{ [key: string]: SurveyDetail }> {
    const surveyConf = await this.getSurveyConf(clientId);
    const patientSurveyKeys = Object.keys(surveyConf['surveys']).filter((survey) => survey.includes('patient'));
    const patientSurveys = patientSurveyKeys.reduce((acc, survey) => {
      if (surveyConf['surveys'][survey]) {
        acc[survey] = surveyConf['surveys'][survey];
      }
      return acc;
    }, {});
    return patientSurveys;
  }

  /**
   * Retrieves a survey URL by making a request to the Typeform API endpoint.
   * In non-production environments, it uses a test survey form ID.
   *
   * @param surveyId - The ID of the survey form to be used in production
   * @param clientId - The ID of the client requesting the survey
   * @param patientId - Optional. The ID of the patient if the survey is patient-specific
   * @returns An Observable that resolves to the survey URL response
   */
  getSurveyUrl(surveyId: string, clientId: string, patientId?: string): Observable<any> {
    const formId = environment.production ? surveyId : this.surveyFormTest;
    return this.http.post<any>(`${environment.welbyEndpoint}/api/v1/typeform/form-url`, {
      formId,
      clientId,
      patientId,
    });
  }

  async saveSurveyConf(clientId: string, surveyConf: SurveyConf): Promise<void> {
    await this.fs.set(`client_survey_conf/${clientId}`, surveyConf);
  }

  patientNameCapitalize(name: string, truncatedSecondWord: boolean) {
    return name
      .split(' ')
      .filter((names) => names !== '')
      .map((n, index) => {
        if (index === 0) {
          return n.charAt(0).toUpperCase() + n.slice(1).toLowerCase();
        } else if (truncatedSecondWord) {
          return this.validateSpecialCharacters(n).charAt(0).toUpperCase() + '.';
        } else {
          return this.validateSpecialCharacters(n).charAt(0).toUpperCase() + n.slice(1).toLowerCase();
        }
      })
      .join(' ');
  }
  validateSpecialCharacters(name: string): string {
    return name.startsWith("'") || name.startsWith('(') || name.startsWith('*') ? name.slice(1) : name;
  }

  getDateFromDOB(dob: Date | string | any): Date {
    if (dob instanceof Date) {
      return dob;
    } else if (dob instanceof firebase.firestore.Timestamp) {
      return dob.toDate();
    } else if (typeof dob === 'string') {
      return new Date(dob);
    }
  }

  getProviderRoles(): Observable<string[]> {
    return this.fs
      .doc('welby_configuration/provider_roles')
      .valueChanges()
      .pipe(map((data: any) => data?.roles));
  }

  getSupplyOrdersEnabled(): Observable<boolean> {
    return this.fs
      .doc('welby_configuration/admin_inventory')
      .valueChanges()
      .pipe(map((data: any) => data?.supply_orders_enabled ?? false));
  }

  getDeviceStatus(deviceStatusInfo: ActiveDeviceStatus | null | undefined, tz?: TimeZone): DeviceStatus {
    // Default values for device status
    const timezone = tz || { nameValue: 'UTC' };
    const defaultStatus: DeviceStatus = {
      deviceStatus: 'Unknown',
      suspendDate: null,
      daysToActivate: '',
      last_checkin_time: null,
      last_signal_strength: null,
      signal_icon: 'signal_cellular_nodata',
      signal_color: 'default',
    };

    if (!deviceStatusInfo) {
      return defaultStatus;
    }

    try {
      if (isSmartMeterStatus(deviceStatusInfo)) {
        const { details } = deviceStatusInfo;
        return {
          ...defaultStatus,
          deviceStatus: deviceStatusInfo.text || 'No Status',
          suspendDate: details?.suspend_date ? moment.tz(details.suspend_date, timezone?.nameValue).toDate() : null,
          daysToActivate: details?.days_to_activate ? `(${details.days_to_activate} Days to Activate)` : '',
        };
      }

      if (isTenoviGateway(deviceStatusInfo)) {
        const maxSignalStrength = 31;
        return {
          ...defaultStatus,
          deviceStatus: deviceStatusInfo.text || 'Gateway Status',
          suspendDate: null,
          daysToActivate: '',
          last_checkin_time: deviceStatusInfo.last_checkin_time
            ? moment.tz(deviceStatusInfo.last_checkin_time, timezone?.nameValue).format('MM/DD/YY hh:mm:ss A z')
            : null,
          last_signal_strength: deviceStatusInfo.last_signal_strength
            ? Number(((deviceStatusInfo.last_signal_strength * 100) / maxSignalStrength).toFixed(2))
            : null,
          last_signal_strength_legend: this.getSignalLegend(deviceStatusInfo.last_signal_strength),
          signal_icon: this.getSignalIcon(deviceStatusInfo.last_signal_strength),
          signal_color: this.getSignalColor(deviceStatusInfo.last_signal_strength),
        };
      }
    } catch (error) {
      console.error('Error processing device status:', error);
    }

    // Handle unexpected device status type
    console.warn('Unknown or invalid device status type:', deviceStatusInfo);
    return defaultStatus;
  }

  getSignalIcon(signalStrength: number): string {
    if (signalStrength >= 20) {
      return 'signal_cellular_alt';
    } else if (signalStrength >= 15) {
      return 'signal_cellular_alt_2_bar';
    } else if (signalStrength >= 10) {
      return 'signal_cellular_1_bar';
    } else if (signalStrength >= 0) {
      return 'signal_cellular_0_bar';
    } else {
      return 'signal_cellular_nodata';
    }
  }

  getSignalColor(signalStrength: number): string {
    if (signalStrength >= 20) {
      return 'primary';
    } else if (signalStrength >= 15) {
      return 'accent';
    } else if (signalStrength >= 10) {
      return 'warn';
    } else {
      return 'default';
    }
  }

  getSignalLegend(signalStrength: number): string {
    if (signalStrength >= 20) {
      return 'Great signal strength: Gateway should connect quickly every time';
    } else if (signalStrength >= 15) {
      return 'Adequate signal strength: Gateway should connect but may take time';
    } else if (signalStrength >= 10) {
      return `Low signal strength: Gateway may take a long time to connect or may not connect at all. 
      Ask them to move the gateway to a window in another section of the house. If this doesn’t work, user must use the Tenovi app`;
    } else if (signalStrength >= 0) {
      return `Very low: There will likely be issues with the gateway. If there are issues, set them up with cellphone app. 
      Ask users to to move the gateway to a window in another section of the house. If this doesn’t work, the user must use the Tenovi app.`;
    } else {
      return 'No Signal';
    }
  }

  /**
   *
   * Returns the AWV interpretation result based on the score.
   *
   * @param interpretation Array of interpretation objects.
   * @param score The score to be evaluated.
   */
  getResult(interpretation: Interpretation[], score: number): Interpretation | undefined {
    return interpretation.find((item) => {
      const [min, max] = item.scoreRange?.split('-').map(Number) || [];
      return score >= min && score <= max;
    });
  }

  getBulkLoadExecution(executionId: string): Observable<any> {
    return this.fs.col('admin_bulk_load_executions').doc(executionId).valueChanges();
  }

  getStatesWithoutCompactLicense(): Observable<{ states: { abbreviation: string; name: string }[] }> {
    return this.fs
      .col('welby_configuration')
      .doc('states_without_compact_license')
      .get()
      .pipe(
        map((snapshot: any) => snapshot.data() as { states: [] }),
        catchError((error) => {
          console.error('Error fetching states_withoutCompactLicense:', error);
          return from([{ states: [] } as { states: [] }]);
        })
      );
  }

  /**
   * Generates an array of time strings in 12-hour format with 30-minute intervals.
   * Creates times from 12:00 AM to 11:30 PM.
   *
   * @returns An array of formatted time strings (e.g., ['12:00 AM', '12:30 AM', ...])
   */
  generateHours(): string[] {
    const out: string[] = [];
    for (let i = 0; i < 48; i++) {
      const totalMinutes = i * 30;
      const hour24 = Math.floor(totalMinutes / 60) % 24;
      const minute = totalMinutes % 60;

      const period = hour24 < 12 ? 'AM' : 'PM';
      let hour12 = hour24 % 12;
      if (hour12 === 0) {
        hour12 = 12;
      }
      const hh = hour12.toString().padStart(2, '0');
      const mm = minute === 0 ? '00' : '30';

      out.push(`${hh}:${mm} ${period}`);
    }
    return out;
  }

  /**
   * Formats a phone number for display in a conference calling interface
   *
   * @param control The form control containing the phone number
   */
  formatPhoneNumberConferenceCalling(control: UntypedFormControl): void {
    if (control.valid && control.value) {
      const formatted = this.formatPhoneNumberStringConferenceCalling(control.value);
      control.setValue(formatted, { emitEvent: false });
    }
  }
  /**
   * Formats a phone number string for conference calling display
   *
   * @param phone The phone number string to format
   * @returns Formatted phone number string
   */
  formatPhoneNumberStringConferenceCalling(phone: string): string {
    const cleaned = ('' + phone).replace(/\D/g, '');
    const isTollFree = /^1?800/.test(cleaned);
    if (isTollFree) {
      const match = cleaned.replace(/^1?800/, '800').match(/^(\d{3})(\d{3})(\d{4})$/);
      if (match) {
        return `(${match[1]}) ${match[2]}-${match[3]}`;
      }
    } else {
      const match = cleaned.replace(/^1?/, '').match(/^(\d{3})(\d{3})(\d{4})$/);
      if (match) {
        return `(${match[1]}) ${match[2]}-${match[3]}`;
      }
    }
    return phone;
  }

  /**
   * A measurement as a clinician expects to read it: at most two decimals, and no trailing zeros —
   * "120", not "120.00"; "1.9", not "1.9000000000000057".
   *
   * The long form is not bad data. A weight delta is a plain subtraction of two readings, and in
   * IEEE-754 `100 - 98.1` is exactly `1.9000000000000057`; the branches above used to
   * string-concatenate that straight into the cell, which put fourteen junk decimals in front of an
   * NCM next to a Low tier chip. Two decimals is past the precision of any device we ingest (scales
   * report tenths of a pound, cuffs and oximeters whole numbers), so nothing real is being hidden.
   *
   * Display only, deliberately: the stored `measurement_value1` and every threshold comparison keep
   * using the exact number, so no escalation band boundary moves because of this.
   *
   * A clean value formats byte-identically to the old concatenation (`120` → "120", `98.6` →
   * "98.6"), so every device alert renders exactly as it does today. A missing or non-numeric value
   * yields '' rather than the literal "undefined" the concatenation produced.
   */
  private formatMeasurement(value: number | undefined | null): string {
    const numeric = Number(value);
    if (value === null || value === undefined || isNaN(numeric)) {
      return '';
    }
    return String(Math.round(numeric * 100) / 100);
  }
}
